import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, useTheme, Tooltip, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';

import { usePagination, Column } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { useSessionFilters } from '@frontend/features/cases/hooks/useSessionFilters';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetHotelSuppliers } from '@frontend/features/hotel-content-manager/queries';
import { client } from '@frontend/features/common/services';

export const HotelSuppliersView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const pagination = usePagination();

  const { data: suppliers, isLoading } = useGetHotelSuppliers({
    currentPage: pagination.page,
    pageSize: pagination.rowsPerPage,
  });

  const { updateFilters, clearFilters } = useSessionFilters(searchParams, setSearchParams);


  const handleUpdateMetadata = async (accessCode: string) => {
    try { 
      await client.post('/content-extractor/extract-metadata', { accessCodes: [accessCode] });
      addToast({
        message: t('hotelContent.management.suppliers.updateMetadataMessages.success'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('hotelContent.management.suppliers.updateMetadataMessages.error'),
        color: 'error',
      });
    }
  };

  const handleRefresh = async () => {
    try {
      await client.post('/content-extractor/extract-suppliers');
      addToast({
        message: t('hotelContent.management.suppliers.refreshMessages.success'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('hotelContent.management.suppliers.refreshMessages.error'),
        color: 'error',
      });
    }
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  // Define columns for the suppliers table
  const columns: Column<any>[] = [
    {
      id: 'supplier',
      label: t('hotelContent.management.suppliers.table.supplier') || 'Supplier',
      align: 'left' as const,
      canSort: true,
      sortField: 'supplierName',
      minWidth: 250,
      format: (_: any, item: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
              {item.supplierName}
            </Box>
            {item.isSupplierActive ? (
              <Box sx={{ color: theme.palette.success.main, fontSize: '1rem' }}>✓</Box>
            ) : (
              <Box sx={{ color: theme.palette.error.main, fontSize: '1rem' }}>✗</Box>
            )}
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
            {item.supplierCode}
          </Box>
        </Box>
      ),
    },
    {
      id: 'provider',
      label: t('hotelContent.management.suppliers.table.provider') || 'Provider',
      align: 'left' as const,
      canSort: true,
      sortField: 'providerName',
      minWidth: 250,
      format: (_: any, item: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>
              {item.providerName}
            </Box>
            {item.isProviderActive ? (
              <Box sx={{ color: theme.palette.success.main, fontSize: '1rem' }}>✓</Box>
            ) : (
              <Box sx={{ color: theme.palette.error.main, fontSize: '1rem' }}>✗</Box>
            )}
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
            {item.providerCode}
          </Box>
        </Box>
      ),
    },
    {
      id: 'accessCode',
      label: 'Access Code',
      align: 'left' as const,
      canSort: true,
      minWidth: 120,
    },
    {
      id: 'supplierContext',
      label: t('hotelContent.management.suppliers.table.supplierContext') || 'Supplier Context',
      align: 'left' as const,
      canSort: true,
      minWidth: 150,
    },
    {
      id: '_',
      label: t('common.fields.actions') || 'Actions',
      align: 'center' as const,
      canSort: false,
      minWidth: 160,
      format: (_: any, item: any) => (
        <Button
          onClick={() => handleUpdateMetadata(item.accessCode)}
          startIcon={<Refresh fontSize="small" />}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.75rem',
            textTransform: 'none',
            minWidth: 'auto',
            px: 1,
          }}
        >
          Update Metadata
        </Button>
      ),
    },
  ];


  const filtersCount = useMemo(() => {
    const keysToOmit = ['sort', 'order', 'page', 'search'];
    let count = 0;

    searchParams.forEach((value, key) => {
      if (!keysToOmit.includes(key) && value) {
        count++;
      }
    });

    return count;
  }, [searchParams]);

  const refreshButton = (
    <Tooltip title={t('common.refresh') || 'Refresh'} arrow>
      <Button
        variant="contained"
        onClick={handleRefresh}
        disabled={isLoading}
        aria-label="Refresh"
        sx={{
          height: theme.spacing(5),
          minWidth: theme.spacing(5),
          padding: theme.spacing(1),
        }}
      >
        <Refresh />
      </Button>
    </Tooltip>
  );

  const filtersOptions = {
    form: (
      <Box sx={{ p: 2, minWidth: 300 }}>
        {/* Add filter form components here when needed */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button onClick={handleClearFilters} variant="outlined">
            {t('common.clearFilters') || 'Clear Filters'}
          </Button>
        </Box>
      </Box>
    ),
    filterListKey: 'supplierslist',
    searchParams,
    setSearchParams,
    onClearFilters: handleClearFilters,
  };

  return (
    <ListLayout
      title={t('hotelContent.management.suppliers.title') || 'Hotel Content Suppliers'}
      subtitle={t('hotelContent.management.suppliers.subtitle') || 'Manage and view hotel content suppliers'}
    >
      <ListContentLayout
        pagination={pagination}
        filtersCount={filtersCount}
        stickyHeader
        filters={filtersOptions}
        onSearchChange={(value: string) => {
          updateFilters({ search: value });
        }}
        statisticsButton={refreshButton}
        columns={columns}
        isLoading={isLoading}
        data={{
          key: 'id',
          items: suppliers?.items || [],
          totalCount: suppliers?.count || 0,
        }}
      />
    </ListLayout>
  );
};
